import content from 'constants/content';
import Empty from 'constants/empty';

const { COMPONENT_HANDLER_TYPES } = content;

/**
 * Detects the `MABDynamicEndDate` handler (PDE-2789) in a `features` array. The handler can be
 * authored either on the CMS component (whole list) or on an individual promotion item, so this is
 * used both against the component's `features` and against each item's `features`.
 *
 * @param {Array<object>} features - Contentful features (component-level or item-level).
 * @returns {boolean}
 */
const hasMabDynamicEndDateHandler = features =>
    Array.isArray(features) && features.some(feature => feature?.handlerType === COMPONENT_HANDLER_TYPES.PROMOTION_LIST.MAB_DYNAMIC_END_DATE);

/**
 * A BO PromotionList is a MAB dynamic-end-date list when the `MABDynamicEndDate` handler is present
 * either on the CMS component's `features` (whole-list override) or on any of its promotion items'
 * `features` (per-item override) (PDE-2789).
 *
 * @param {Array<object>} features - the component's Contentful features.
 * @param {Array<object>} [items] - the list's promotion items (each may carry its own `features`).
 * @returns {boolean}
 */
const isMabDynamicEndDateList = (features, items = Empty.Array) =>
    hasMabDynamicEndDateHandler(features) || (Array.isArray(items) && items.some(item => hasMabDynamicEndDateHandler(item?.features)));

/**
 * Reads `mabBoPromoEndDate` off a resolved p13n entry (PDE-2789). The field sits under `p13n` in
 * the head/cache shape and at the root in the preview (`p13n.data`) shape, so both are checked.
 *
 * @param {object} [entry] - a p13n entry (head item, preview item, or cached personalized component).
 * @returns {string|undefined}
 */
const readMabBoPromoEndDate = entry => entry?.p13n?.mabBoPromoEndDate || entry?.mabBoPromoEndDate;

/**
 * Reads the backend-provided MAB promotion end date for a personalization context from the redux
 * `p13n` slice (PDE-2789). Matches on `entry.p13n.context` (fallback `entry.context`).
 *
 * PME-2113: searches `data` (preview) *and* `headData` rather than picking one list. Preview
 * entries can populate `data` while the BO context only exists in `headData`, and the previous
 * `data.length ? data : headData` short-circuit made the lookup miss and silently fall back to the
 * Contentful end date.
 *
 * @param {object} p13n - the redux `p13n` slice ({ data, headData }).
 * @param {string} contextId - the personalization context id (personalization.context).
 * @returns {string|undefined} mabBoPromoEndDate for that context, if present.
 */
const getMabBoPromoEndDate = (p13n, contextId) => {
    if (!p13n || !contextId) {
        return undefined;
    }

    const matches = item => (item?.p13n?.context || item?.context) === contextId;

    return readMabBoPromoEndDate((p13n.data || Empty.Array).find(matches)) || readMabBoPromoEndDate((p13n.headData || Empty.Array).find(matches));
};

/**
 * Applies the dynamic MAB promotion end date to Beauty Offers list items (PDE-2789). When
 * `mabBoPromoEndDate` is present the backend date overrides the Contentful `endDate`; when it is
 * absent the items are returned unchanged, so the list falls back to its Contentful-configured end
 * date (per PDE-2789 the fallback keeps the current behavior).
 *
 * The handler can be authored at two levels: on the CMS component (`componentFeatures`), in which
 * case the override applies to the whole list, or on individual promotion items (`item.features`),
 * in which case only the flagged items are overridden.
 *
 * @param {Array<object>} items - BO List promotion items.
 * @param {string} [mabBoPromoEndDate] - context-level end date from the p13n BO List API.
 * @param {Array<object>} [componentFeatures] - the component's Contentful features.
 * @returns {Array<object>} items with the MAB end date applied when available.
 */
const applyMabDynamicEndDate = (items = Empty.Array, mabBoPromoEndDate, componentFeatures = Empty.Array) => {
    if (!mabBoPromoEndDate || !Array.isArray(items) || !items.length) {
        return items;
    }

    const isComponentLevel = hasMabDynamicEndDateHandler(componentFeatures);

    return items.map(item => (isComponentLevel || hasMabDynamicEndDateHandler(item?.features) ? { ...item, endDate: mabBoPromoEndDate } : item));
};

export {
    hasMabDynamicEndDateHandler, isMabDynamicEndDateList, readMabBoPromoEndDate, getMabBoPromoEndDate, applyMabDynamicEndDate
};

export default applyMabDynamicEndDate;
